import { ElectronAPI } from '#~/electron/index.js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

export type PermissionState = 'allowed' | 'denied' | 'default';

export interface DerivePermissionStateArgs {
  /** `true` once the OS status query reports the permission granted. */
  data: boolean | undefined;
  /** `true` once the user has triggered the request mutation this session. */
  interacted: boolean;
  /** `true` while the status query/refetch is in flight (react-query `fetchStatus`). */
  isFetching: boolean;
}

/**
 * Derive the tri-state permission label from the query snapshot.
 *
 * ENG-4420: denial must be OBSERVED from a settled status refetch, never INFERRED from
 * "interacted and not yet granted". The request mutation's `onSuccess` flips `interacted`
 * true synchronously but only STARTS the refetch (`refetchQueries`), which synchronously
 * flips `fetchStatus` to 'fetching'. Without the `isFetching` gate, that pending window
 * (`interacted === true`, `data === false`) read as `denied` on every grant, latching a
 * false `permissions_grant_failed`. Gating on `!isFetching` makes the pending window read
 * `default`/pending, so `denied` is concluded only once the refetch has settled false.
 *
 * Pure and exported so the render-ordering fix is unit-testable in isolation — mirrors how
 * use-grant-success-tracking.ts (ENG-4307, the do-not-touch success sibling) factors out
 * `areAllPermissionsGranted`.
 */
export const derivePermissionState = ({
  data,
  interacted,
  isFetching
}: DerivePermissionStateArgs): PermissionState => {
  // A settled positive answer always wins, even if a later background refetch is in flight.
  if (data === true) {
    return 'allowed';
  }
  // Settled-gate (ENG-4420): conclude `denied` ONLY once the status refetch is not in flight.
  // The pending window (`interacted === true`, `data` still false, `isFetching === true`) must
  // read `default`, not `denied` — otherwise every grant latches a false permissions_grant_failed.
  if (interacted && isFetching === false) {
    return 'denied';
  }
  return 'default';
};

const addArtificialDelay =
  <T extends (...args: any[]) => any>(fn: T) =>
  async (...args: Parameters<T>) => {
    const [_, value] = await Promise.all([
      new Promise((resolve) => setTimeout(resolve, 250)),
      fn(...args)
    ]);

    return value as ReturnType<T>;
  };

const permissionsMap = {
  camera: {
    status: () => ElectronAPI.permissions.mediaStatus('camera'),
    request: () => ElectronAPI.permissions.requestMedia('camera'),
    openSettings: () => ElectronAPI.permissions.openSettingsCamera()
  },
  microphone: {
    status: () => ElectronAPI.permissions.mediaStatus('microphone'),
    request: () => ElectronAPI.permissions.requestMedia('microphone'),
    openSettings: () => ElectronAPI.permissions.openSettingsMicrophone()
  },
  network: {
    status: () => ElectronAPI.permissions.networkStatus(),
    request: () => ElectronAPI.permissions.requestNetwork(),
    openSettings: undefined
  },
  startup: {
    status: () => ElectronAPI.permissions.startupStatus(),
    request: (value: boolean) => ElectronAPI.permissions.requestStartup(value),
    openSettings: undefined
  }
} as const;

type Request = typeof permissionsMap;

const usePermission = <K extends keyof typeof permissionsMap>(key: K, enabled = true) => {
  const [interacted, setInteracted] = useState(false);
  const permission = permissionsMap[key];
  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['permissions', key],
    queryFn: permission.status,
    enabled
  });

  const queryClient = useQueryClient();
  const { mutate, mutateAsync, isPending } = useMutation<
    ReturnType<Request[K]['request']>,
    Error,
    Parameters<Request[K]['request']>[0] extends undefined
      ? void
      : Parameters<Request[K]['request']>[0]
  >({
    mutationFn: addArtificialDelay(permission.request as any),
    onSuccess: () => {
      setInteracted(true);
      queryClient.refetchQueries({ queryKey: ['permissions', key] });
    }
  });

  // ENG-4420: settled-gate — never conclude `denied` while the status refetch is in flight.
  const state: PermissionState = derivePermissionState({ data, interacted, isFetching });

  return {
    mutate,
    mutateAsync,
    key,
    enabled,
    permissionState: state,
    state,
    error,
    isLoading: isLoading || isPending,
    openSettings: permission.openSettings
  };
};

export const usePermissions = () => {
  const {
    data: os,
    isFetched: hasOSAttempted,
    isFetching: isOSFetching,
    refetch: retryOS
  } = useQuery({
    queryKey: ['os'],
    queryFn: async () => {
      return ElectronAPI.getOS();
    }
  });

  const camera = usePermission('camera');
  const microphone = usePermission('microphone');
  const network = usePermission('network', os === 'windows');
  const startup = usePermission('startup');

  return {
    // Network is enabled only after OS identification; media alone cannot settle that initial check.
    isOSKnown: os !== undefined,
    // Failed reads retain their recovery UI while retrying: the query resets isError to
    // pending, but its completed-attempt count survives until real OS data arrives.
    osError: os === undefined && hasOSAttempted,
    isOSFetching,
    retryOS,
    permissions: {
      camera,
      microphone,
      network,
      startup
    }
  };
};
